import { create } from 'zustand';
import { getServices } from '../api/generated/services/services';
import { hasFieldErrors } from '../errors/mapFieldErrors';
import { useToastStore } from './useToastStore';
import type {
  FacilityFlowCoreModulesServicesDtosServiceDto,
  FacilityFlowCoreModulesServicesWorkflowsCreateServiceRequest,
  FacilityFlowCoreModulesServicesWorkflowsUpdateServiceRequest,
} from '../api/generated/models';
import { parseAxiosError, translateApiError } from '../errors';

interface ServiceState {
  /** All services. Only `fetchServices` writes it, so pickers on other pages never see a subset. */
  services: FacilityFlowCoreModulesServicesDtosServiceDto[];
  /** Result of the services page's server-side search, kept apart from `services`. */
  searchResults: FacilityFlowCoreModulesServicesDtosServiceDto[];
  loading: boolean;
  error: string | null;

  fetchServices: () => Promise<void>;
  searchServices: (searchTerm: string) => Promise<void>;
  createService: (data: FacilityFlowCoreModulesServicesWorkflowsCreateServiceRequest) => Promise<void>;
  updateService: (id: string, data: FacilityFlowCoreModulesServicesWorkflowsUpdateServiceRequest) => Promise<void>;
  deleteService: (id: string) => Promise<void>;
  getServiceById: (id: string) => FacilityFlowCoreModulesServicesDtosServiceDto | undefined;
}

const serviceApi = getServices();

export const useServiceStore = create<ServiceState>((set, get) => ({
  services: [],
  searchResults: [],
  loading: false,
  error: null,

  fetchServices: async () => {
    set({ loading: true, error: null });
    try {
      console.log('[ServiceStore] Fetching services from API');
      const response = await serviceApi.getApiV1Services();
      console.log('[ServiceStore] Received services:', response);
      set({ services: response || [], loading: false });
    } catch (error) {
      console.error('[ServiceStore] Error fetching services:', error);
      const errorMessage = error instanceof Error ? error.message : 'Fehler beim Laden der Dienstleistungen';
      set({ error: errorMessage, loading: false });
      // Kein Toast beim Fetch - Error-State wird in der UI angezeigt
    }
  },

  searchServices: async (searchTerm: string) => {
    set({ loading: true, error: null });
    try {
      const response = await serviceApi.getApiV1Services({ searchTerm });
      set({ searchResults: response || [], loading: false });
    } catch (error) {
      set({ error: translateApiError(parseAxiosError(error)), loading: false });
    }
  },

  createService: async (data: FacilityFlowCoreModulesServicesWorkflowsCreateServiceRequest) => {
    set({ loading: true, error: null });
    try {
      await serviceApi.postApiV1Services(data);
      await get().fetchServices();
      useToastStore.getState().addToast(
        'Dienstleistung erfolgreich erstellt',
        'success'
      );
    } catch (error) {
      const errorMessage = (error as Error).message;
      set({ error: errorMessage, loading: false });
      if (!hasFieldErrors(error)) {
        useToastStore.getState().addToast(
          'Fehler beim Erstellen der Dienstleistung',
          'error'
        );
      }
      throw error;
    }
  },

  updateService: async (id: string, data: FacilityFlowCoreModulesServicesWorkflowsUpdateServiceRequest) => {
    set({ loading: true, error: null });
    try {
      await serviceApi.putApiV1ServicesId(id, data);
      await get().fetchServices();
      useToastStore.getState().addToast(
        'Dienstleistung erfolgreich aktualisiert',
        'success'
      );
    } catch (error) {
      const errorMessage = (error as Error).message;
      set({ error: errorMessage, loading: false });
      if (!hasFieldErrors(error)) {
        useToastStore.getState().addToast(
          'Fehler beim Aktualisieren der Dienstleistung',
          'error'
        );
      }
      throw error;
    }
  },

  deleteService: async (id: string) => {
    set({ loading: true, error: null });
    try {
      await serviceApi.deleteApiV1ServicesId(id);
      set({
        services: get().services.filter((s) => s.id !== id),
        searchResults: get().searchResults.filter((s) => s.id !== id),
        loading: false,
      });
      useToastStore.getState().addToast(
        'Dienstleistung erfolgreich gelöscht',
        'success'
      );
    } catch (error) {
      const errorMessage = (error as Error).message;
      set({ error: errorMessage, loading: false });
      useToastStore.getState().addToast(
        'Fehler beim Löschen der Dienstleistung',
        'error'
      );
      throw error;
    }
  },

  getServiceById: (id: string) => {
    return get().services.find((s) => s.id === id);
  },
}));
